import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { focusRing, pressable } from '../../../styles/visualLanguage';
import { mapAdvisoryErrorCode } from '../../../redux/slices/advisory/advisoryErrorCodes';
import type { AdvisoryDocumentRecord } from '../../../services/advisoryDocumentService';

/**
 * One row per document the user added, with what became of it.
 *
 * Purely presentational — no hook, no service, no fetch. It takes records and two
 * callbacks, which is what lets it be rendered from a fixture in a test without a
 * store, a token or a network.
 *
 * **It never renders an error message from a server.** A failed record carries a
 * `errorCode`, and this component resolves it through `mapAdvisoryErrorCode`, which
 * can only ever return one of a fixed set of pre-authored entries (ASVS V7). The
 * same mapping supplies `retryable`, which is why a rejected file type shows no
 * retry button: offering one that can never succeed is worse than offering none.
 */
export interface AdvisoryDocumentListProps {
  documents: readonly AdvisoryDocumentRecord[];
  onRetry: (documentId: string) => void;
  onRemove: (documentId: string) => void;
}

function stateLine(record: AdvisoryDocumentRecord): {
  key: string;
  options?: Record<string, number>;
} {
  switch (record.state) {
    case 'uploading':
      return { key: 'advisory:evidence.stateUploading' };
    case 'ingesting':
      return { key: 'advisory:evidence.stateIngesting' };
    case 'ready':
      // Counts come from docintel and are the only honest evidence the document was
      // actually read. When it reported neither, say "Ready" rather than "0 pages · 0
      // sections", which would read as an empty document.
      return record.pageCount !== null || record.chunkCount !== null
        ? {
            key: 'advisory:evidence.stateReady',
            options: { pages: record.pageCount ?? 0, chunks: record.chunkCount ?? 0 },
          }
        : { key: 'advisory:evidence.stateReadyPlain' };
    case 'failed':
    default:
      return { key: 'advisory:evidence.stateFailed' };
  }
}

export function AdvisoryDocumentList({ documents, onRetry, onRemove }: AdvisoryDocumentListProps) {
  const { t } = useUntypedTranslation();

  if (documents.length === 0) {
    return null;
  }

  return (
    <Box
      component="ul"
      data-testid="advisory-document-list"
      sx={{ listStyle: 'none', margin: 0, padding: 0 }}
    >
      {documents.map(record => {
        const line = stateLine(record);
        const failure = record.errorCode ? mapAdvisoryErrorCode(record.errorCode) : null;
        const terminal = record.state === 'ready' || record.state === 'failed';

        return (
          <Box
            component="li"
            key={record.documentId}
            data-testid="advisory-document-row"
            data-state={record.state}
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              paddingY: 1,
              borderBottom: `1px solid ${theme.visualPrimitives.border}`,
              textAlign: 'left',
            })}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={theme => ({
                  fontSize: theme.visualPrimitives.dzSubSize,
                  color: theme.colors.inkPrimary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {record.fileName}
              </Typography>
              <Typography
                // `role="status"` so a screen reader is told when a long ingest
                // finishes, rather than the change passing silently.
                role="status"
                aria-live="polite"
                sx={theme => ({
                  fontSize: theme.visualPrimitives.dzSubSize,
                  color: theme.colors.inkTertiary,
                })}
              >
                {t(line.key, line.options)}
                {/* docintel matches by content, so identical bytes resolve onto the
                    document already ingested. Saying so is what makes a low chunk
                    count answerable rather than mysterious. */}
                {record.canonicalDocumentId && record.canonicalDocumentId !== record.documentId
                  ? ` · ${t('advisory:evidence.duplicate')}`
                  : ''}
              </Typography>
              {failure ? (
                <Typography
                  data-testid="advisory-document-error"
                  sx={theme => ({
                    fontSize: theme.visualPrimitives.dzSubSize,
                    color: theme.colors.inkTertiary,
                  })}
                >
                  {t(failure.i18nKey)}
                </Typography>
              ) : null}
            </Box>

            {failure?.retryable ? (
              <Button
                variant="text"
                disableRipple
                data-testid="advisory-document-retry"
                onClick={() => onRetry(record.documentId)}
                sx={[pressable, focusRing]}
              >
                {t('advisory:evidence.retryAction')}
              </Button>
            ) : null}

            {/* A ready document is already in the collection and this client has no
                erasure path, so removing the row would claim an un-ingest that did
                not happen. Only a non-ready row offers the control. */}
            {terminal && record.state !== 'ready' ? (
              <Button
                variant="text"
                disableRipple
                data-testid="advisory-document-remove"
                onClick={() => onRemove(record.documentId)}
                sx={[pressable, focusRing]}
              >
                {t('advisory:evidence.removeAction')}
              </Button>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

export default AdvisoryDocumentList;
